/**
 * 智能助手（P-D④，蓝图 §4「会话列表 | 技能 | 创作/视频/图片/音乐 | 画布」）。
 *
 * 全出血双栏：左会话列表（新建/切换/删除，本地持久化）+ 右对话区（模式 tab、消息流、输入）。
 * 助手回复为 mockAgentReply（占位编排），接真 LLM 后换 provider 即可，UI 不变。
 * 「按此规划新建画布」把 Agent 接到创作总线。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, Send, Sparkles, Trash2 } from 'lucide-react';

import {
  deleteSession,
  listSessions,
  loadSession,
  mockAgentReply,
  newSessionId,
  saveSession,
  sessionTitle,
  type AgentSession,
} from '../../model/agent';
import { createEmptyCanvas } from '../../model/canvas';
import { newCanvasId } from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';

const MODES = ['创作', '图片', '视频', '音乐'] as const;

function when(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) {
    return '';
  }
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  return new Date(t).toLocaleDateString();
}

export function AgentPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<AgentSession[]>(() => listSessions());
  const [currentId, setCurrentId] = useState<string | null>(() => listSessions()[0]?.id ?? null);
  const [current, setCurrent] = useState<AgentSession | null>(() => {
    const first = listSessions()[0];
    return first !== undefined ? loadSession(first.id) : null;
  });
  const [mode, setMode] = useState<string>('创作');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [current?.messages.length, sending]);

  function refreshList(): void {
    setSessions(listSessions());
  }

  function newConversation(): void {
    const s: AgentSession = { id: newSessionId(), title: '新对话', mode: '创作', messages: [], updatedAt: new Date().toISOString() };
    saveSession(s);
    setCurrent(s);
    setCurrentId(s.id);
    setMode('创作');
    setInput('');
    refreshList();
  }

  function openSession(id: string): void {
    const s = loadSession(id);
    if (s === null) {
      refreshList();
      return;
    }
    setCurrent(s);
    setCurrentId(id);
    setMode(s.mode);
  }

  function removeConversation(e: React.MouseEvent, id: string): void {
    e.stopPropagation();
    deleteSession(id);
    refreshList();
    if (currentId === id) {
      const next = listSessions()[0];
      if (next !== undefined) {
        openSession(next.id);
      } else {
        setCurrent(null);
        setCurrentId(null);
      }
    }
  }

  async function send(): Promise<void> {
    const text = input.trim();
    if (text === '' || sending) {
      return;
    }
    const base: AgentSession = current ?? { id: newSessionId(), title: '新对话', mode, messages: [], updatedAt: new Date().toISOString() };
    const now = new Date().toISOString();
    const withUser: AgentSession = { ...base, mode, messages: [...base.messages, { role: 'user', content: text, ts: now }] };
    setCurrent(withUser);
    setCurrentId(withUser.id);
    setInput('');
    setSending(true);
    await new Promise((r) => setTimeout(r, 500));
    const reply: AgentSession = {
      ...withUser,
      messages: [...withUser.messages, { role: 'assistant', content: mockAgentReply(text, mode), ts: new Date().toISOString() }],
    };
    saveSession(reply);
    setCurrent(reply);
    setSending(false);
    refreshList();
  }

  function startCanvas(): void {
    useCanvasStore.getState().setDoc(createEmptyCanvas(newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }

  const lastAssistantIsPlan = useMemo(() => {
    const last = current?.messages[current.messages.length - 1];
    return last?.role === 'assistant';
  }, [current]);

  const hasMessages = current !== null && current.messages.length > 0;

  // 输入区（空态居中带跑马发光 / 对话态沉底）复用同一 composer
  const composer = (
    <div className="flex items-end gap-2 rounded-card border border-surface-edge bg-surface-overlay p-2">
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
          }
        }}
        rows={2}
        placeholder="描述你的故事想法，或想生成的内容…（Enter 发送，Shift+Enter 换行）"
        className="max-h-40 min-h-[52px] flex-1 resize-none bg-transparent px-1.5 py-2 text-ui leading-relaxed text-ink-primary outline-none placeholder:text-ink-faint"
      />
      <button type="button" onClick={() => void send()} disabled={sending || input.trim() === ''} aria-label="发送" className="btn-primary flex h-9 w-9 shrink-0 items-center justify-center">
        <Send size={16} />
      </button>
    </div>
  );

  return (
    <div className="flex h-full overflow-hidden">
      {/* 左：会话列表 */}
      <aside className="flex w-[280px] shrink-0 flex-col border-r border-surface-line bg-surface-raised/60">
        <div className="p-3">
          <button type="button" onClick={newConversation} className="btn-primary inline-flex w-full items-center justify-center gap-1.5 py-2">
            <Plus size={15} />
            新建对话
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {sessions.length === 0 ? (
            <p className="px-2 py-6 text-center text-uiSm text-ink-faint">还没有对话。点「新建对话」开始。</p>
          ) : (
            sessions.map((s) => {
              const preview = s.messages[s.messages.length - 1]?.content.slice(0, 24) ?? '（空）';
              const on = s.id === currentId;
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openSession(s.id)}
                  onKeyDown={(e) => e.key === 'Enter' && openSession(s.id)}
                  className={`group mb-1 flex cursor-pointer flex-col gap-0.5 rounded-control px-2.5 py-2 transition-colors ${on ? 'bg-surface-overlay' : 'hover:bg-surface-overlay/60'}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-ui font-semibold text-ink-primary">{sessionTitle(s)}</span>
                    <button type="button" aria-label="删除对话" onClick={(e) => removeConversation(e, s.id)} className="ml-auto shrink-0 rounded p-1 text-ink-faint opacity-0 transition-opacity hover:text-state-danger group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-uiSm text-ink-muted">
                    <span className="truncate">{preview}</span>
                    <span className="ml-auto shrink-0 text-ink-faint">{when(s.updatedAt)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* 右：对话区 */}
      <main className="flex min-w-0 flex-1 flex-col">
        {/* 模式 tab */}
        <div className="flex items-center gap-1.5 border-b border-surface-line px-5 py-2.5">
          <Bot size={16} className="text-accent" />
          <span className="mr-2 text-ui font-semibold">智能助手</span>
          {MODES.map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} className={`rounded-control px-2.5 py-1 text-uiSm transition-colors ${mode === m ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-secondary hover:text-ink-primary'}`}>
              {m}
            </button>
          ))}
        </div>

        {!hasMessages ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-16">
            <div className="text-center">
              <div className="mb-3 flex justify-center">
                <Sparkles size={30} className="text-accent" />
              </div>
              <h2 className="text-2xl font-semibold">你想创作什么，交给 Agent</h2>
              <p className="mx-auto mt-2 max-w-md text-ui text-ink-muted">用一句话描述故事想法，我会规划整条创作流程（剧本→资产→分镜→生成→成片）。接入真实 LLM 后自动落画布。</p>
            </div>
            <div className="agent-glow w-full max-w-2xl rounded-card">{composer}</div>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="mx-auto flex max-w-3xl flex-col gap-3">
                {current?.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] whitespace-pre-wrap rounded-card px-3.5 py-2.5 text-ui leading-relaxed ${m.role === 'user' ? 'bg-accent/15 text-ink-primary' : 'bg-surface-overlay text-ink-primary'}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="rounded-card bg-surface-overlay px-3.5 py-2.5 text-ui text-ink-muted">思考中…</div>
                  </div>
                )}
                {!sending && lastAssistantIsPlan && (
                  <div className="flex justify-start">
                    <button type="button" onClick={startCanvas} className="btn-ghost inline-flex items-center gap-1.5 text-uiSm">
                      <Sparkles size={14} />
                      按此规划新建画布
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 pb-8 pt-2">
              <div className="mx-auto w-full max-w-3xl">{composer}</div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
